/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useDeleteFilesMutation } from '@gen2/api/files/hooks';
import {
  FollowUpKeys,
  useDuplicateFollowUpGroupRequestMutation,
  useUpdateFollowUpRequestGroupNameMutation,
} from '@gen2/api/follow-up/hooks';
import {
  TInviteRequest,
  TInviteRequestGroup,
} from '@gen2/api/invite-requests/api';
import { useDeleteInviteRequestMutation } from '@gen2/api/invite-requests/hooks';
import { EditableTitle } from '@gen2/app/components/editable-title/editable-title';
import { useEditableTitle } from '@gen2/app/components/editable-title/useEditableTitle';
import LoadTemplate from '@gen2/app/components/load-template/load-template';
import { useOpenSharedDrawer } from '@gen2/app/components/organizing-request-modal/hooks/useOpenSharedDrawer';
import { validateGroupName } from '@gen2/app/components/organizing-request-modal/utils';
import { useSendInviteStore } from '@gen2/app/invites/send-invites/store';
import { queryClient } from '@gen2/config';
import { useToast } from '@gen2/hooks';
import { PermissionsKeys } from '@gen2/types/permissions';
import { Permissions } from '@gen2/utils/permissions/permissions';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/material';
import { rem } from 'polished';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useUnmount } from 'usehooks-ts';
import { useConfirmationModalStore } from '../components/confirmation-modal/hooks/confirmation-modal-store';
import { useFileStore } from '../components/invite-request/hooks/file-store';
import { InviteRequest } from '../components/invite-request/invite-request';
import { OrganizeRequestButton } from '../components/organizing-request-modal/organize-request-button';
import {
  defaultRequests,
  TInviteRequestsForm,
} from '../invites/send-invites/requests/requests';
import {
  StyledRequests,
  StyledRequestsHead,
} from '../invites/send-invites/requests/requests.styled';
import { inviteRequestsSchema } from '../invites/send-invites/requests/schema';
import { useRequestsStore } from '../invites/send-invites/requests/store';
import { Title } from './follow-up.styled';

export const FollowUpRequestsContent = ({
  disabled,
  initialRequests,
  requestGroups,
}: {
  disabled: boolean;
  initialRequests: TInviteRequest[];
  requestGroups: TInviteRequestGroup[];
}) => {
  const store = useSendInviteStore();

  const { inviteId, followUpId } = useParams<{
    inviteId: string;
    followUpId: string;
  }>();
  const toast = useToast();
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editTitleError, setEditTitleError] = useState<string>('');

  const { mutateAsync: updateFollowUpRequestGroupNameMutation } =
    useUpdateFollowUpRequestGroupNameMutation();

  const { mutate: duplicateFollowUpGroupRequestMutation } =
    useDuplicateFollowUpGroupRequestMutation();

  const openSharedDrawer = useOpenSharedDrawer();

  const handleOpenDrawer = useCallback(() => {
    openSharedDrawer({
      type: 'followup',
      inviteId: inviteId!,
      followUpId: followUpId!,
      items: initialRequests,
      itemsToGroup: requestGroups,
    });
  }, [openSharedDrawer, inviteId, followUpId, initialRequests, requestGroups]);

  const handleTitleSave = useCallback(
    async (groupId: string, newTitle: string) => {
      await updateFollowUpRequestGroupNameMutation(
        {
          inviteId: inviteId!,
          followUpId: followUpId!,
          requestGroupId: groupId,
          data: { name: newTitle },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([FollowUpKeys.getFollowUp]);
          },
        },
      );
    },
    [updateFollowUpRequestGroupNameMutation, inviteId, followUpId],
  );

  const handleDuplicateGroupForFollowUp = useCallback(
    (groupId: string) => {
      duplicateFollowUpGroupRequestMutation(
        {
          inviteId: inviteId!,
          followUpId: followUpId!,
          requestGroupId: groupId,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([FollowUpKeys.getFollowUp]);
          },
        },
      );
    },
    [duplicateFollowUpGroupRequestMutation, inviteId, followUpId],
  );

  // Calculate total number of follow-up requests
  const totalRequestsCount = useMemo(() => {
    const groupedRequestsCount = requestGroups.reduce(
      (total, group) => total + (group.requests?.length || 0),
      0,
    );
    return groupedRequestsCount + initialRequests.length;
  }, [requestGroups, initialRequests]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: rem(24) }}>
      <Title>Add Follow-Up Requests</Title>
      <LoadTemplate handleClick={() => store.setIsTemplatesOpen(true)} />
      <Permissions allow={PermissionsKeys.REORDER_FOLLOW_UP}>
        <OrganizeRequestButton
          onClick={handleOpenDrawer}
          mt={16}
          disabled={disabled}
          isContextReady={Boolean(
            initialRequests.length || requestGroups.length,
          )}
        />
      </Permissions>

      {requestGroups &&
        requestGroups.length > 0 &&
        requestGroups.map((group: TInviteRequestGroup) => (
          <div key={group.id}>
            <InviteRequestsListWithForm
              key={
                group.id +
                '-' +
                (group.requests?.map((r) => r.id).join(',') ?? '')
              }
              title={group.name}
              data={group.requests}
              hasDelete={totalRequestsCount > 1}
              isGroup={true}
              groupId={group.id}
              onUpdateGroupTitle={handleTitleSave}
              onDuplicateGroup={handleDuplicateGroupForFollowUp}
            />
          </div>
        ))}
      {initialRequests.length > 0 && (
        <InviteRequestsListWithForm
          key={'initial-' + initialRequests.map((r) => r.id).join(',')}
          data={initialRequests}
          hasDelete={totalRequestsCount > 1}
        />
      )}
    </Box>
  );
};

export const FollowUpRequestsList = ({
  data,
  title,
  hasDelete,
  isGroup,
  groupId,
  onUpdateGroupTitle,
  onDuplicateGroup,
}: {
  data: TInviteRequest[];
  title?: string;
  hasDelete?: boolean;
  isGroup?: boolean;
  groupId?: string;
  onUpdateGroupTitle?: (groupId: string, newTitle: string) => void;
  onDuplicateGroup?: (groupId: string) => void;
}) => {
  const { fields: requests } = useFieldArray({
    name: 'requests',
  });

  const { getValues, reset } = useFormContext<TInviteRequestsForm>();
  const { toDeleteRequest, setToDeleteRequest } = useRequestsStore();
  const { setIsConfirmationModalOpen, setConfirmationModalProps } =
    useConfirmationModalStore();
  const { mutate: deleteInviteRequestMutation, isLoading: isRequestDeleting } =
    useDeleteInviteRequestMutation();
  const toast = useToast();
  const { t } = useTranslation('sendInvite');
  const { inviteId } = useParams<{ inviteId: string }>();
  const { file: selectedFile, setFile: setSelectedFile } = useFileStore();
  const { mutate: deleteFilesMutation, isLoading: isFileDeleting } =
    useDeleteFilesMutation();

  const editableTitle = useEditableTitle({
    title,
    isGroup,
    groupId,
    onUpdateGroupTitle,
    validateGroupName,
  });

  // Use useWatch to only watch the expanded fields, preventing re-renders on other field changes
  const watchedRequests = useWatch<TInviteRequestsForm>({
    name: 'requests',
  }) as TInviteRequest[];
  const expandedStates =
    watchedRequests?.map((req: any) => req?.expanded) || [];

  // Get current requests data without watching to avoid re-renders
  const getCurrentRequests = useCallback(() => {
    return getValues('requests');
  }, [getValues]);

  useEffect(() => {
    if (!data?.length) {
      reset({ requests: [] });
      return;
    }

    const currentRequests = getCurrentRequests();
    const currentExpansionStates =
      currentRequests?.reduce(
        (acc, req, index) => {
          if (req.expanded !== undefined) {
            acc[index] = req.expanded;
          }
          return acc;
        },
        {} as Record<number, boolean>,
      ) || {};

    const requestsWithPreservedExpansion = data.map((req, index) => ({
      ...req,
      expanded: currentExpansionStates[index] ?? (req as any).expanded ?? false,
    }));

    reset({ requests: requestsWithPreservedExpansion });
  }, [data, reset, getCurrentRequests]);

  useUnmount(() => {
    // Cleanup logic here
    reset(defaultRequests);
  });

  // Calculate expand all button state
  const expandAllButtonState = useMemo(
    () =>
      expandedStates.some((expanded: boolean) => expanded === false)
        ? 'expand'
        : 'collapse',
    [expandedStates],
  );

  // Handle expand/collapse all requests in the group
  const handleExpandCollapseAll = useCallback(() => {
    const currentRequests = watchedRequests;
    if (!currentRequests?.length) return;

    const allRequestsExpanded = currentRequests.every(
      (req: any) => req.expanded === true,
    );

    // Simple toggle: if all are expanded, collapse all; otherwise expand all
    const newExpandedState = !allRequestsExpanded;

    // Update all individual expanded states
    const updatedRequests = currentRequests.map((req: any) => ({
      ...req,
      expanded: newExpandedState,
    }));

    reset({ requests: updatedRequests });
  }, [watchedRequests, reset]);

  const requestTitle = useMemo(() => {
    if (requests.length > 0) {
      return toDeleteRequest?.title ?? '';
    }

    return '';
  }, [toDeleteRequest, requests]);

  // Memoize request components
  const memoizedRequests = useMemo(() => {
    const currentRequests = watchedRequests;

    if (!currentRequests) return null;

    return currentRequests.map((request: TInviteRequest, index: number) => {
      return (
        <InviteRequest
          key={request?.id || `request-${index}`}
          id={index}
          container="follow-up"
          hasDelete={hasDelete}
          isDeleteDisabled={isRequestDeleting}
        />
      );
    });
  }, [hasDelete, isRequestDeleting, watchedRequests]);

  const onDelete = useCallback(() => {
    if (toDeleteRequest) {
      deleteInviteRequestMutation(
        {
          inviteId: inviteId!,
          requestIds: [toDeleteRequest.id],
        },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries([FollowUpKeys.getFollowUp]);

            toast.show({
              text: t('request.delete.success'),
              variant: 'success',
            });

            return null;
          },
          onError: () => {
            toast.show({
              text: t('request.delete.error'),
              variant: 'error',
            });
          },
          onSettled: () => {
            setToDeleteRequest(undefined);
            setIsConfirmationModalOpen(false);
          },
        },
      );
    }
  }, [
    deleteInviteRequestMutation,
    toDeleteRequest,
    inviteId,
    setIsConfirmationModalOpen,
    toast,
    t,
  ]);

  const handleFileDelete = useCallback(() => {
    if (selectedFile) {
      deleteFilesMutation(
        {
          type: 'request',
          typeId: selectedFile.request.id,
          files: [{ id: selectedFile.id }],
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([FollowUpKeys.getFollowUp]);

            toast.show({
              text: t('file.delete.success'),
              variant: 'success',
            });
          },
          onError: () => {
            toast.show({
              text: t('file.delete.error'),
              variant: 'error',
            });
          },
          onSettled: () => {
            setSelectedFile(undefined);
            setIsConfirmationModalOpen(false);
          },
        },
      );
    }
  }, [selectedFile, deleteFilesMutation, toast, t]);

  useEffect(() => {
    if (!requestTitle) return;

    setConfirmationModalProps({
      onClose: () => setIsConfirmationModalOpen(false),
      onSubmit: onDelete,
      isLoading: isRequestDeleting,
      title: t('request.delete.title'),
      submitLabel: t('request.delete.buttonLabel'),
      submitColor: 'danger',
      message: t('request.delete.message', {
        requestName: requestTitle,
      }),
    });
  }, [
    isRequestDeleting,
    onDelete,
    requestTitle,
    setConfirmationModalProps,
    setIsConfirmationModalOpen,
    t,
  ]);

  useEffect(() => {
    if (!selectedFile) return;

    const isDeleteFileFromFormType = selectedFile.request.type === 'live-form';

    setConfirmationModalProps({
      onClose: () => setIsConfirmationModalOpen(false),
      onSubmit: handleFileDelete,
      isLoading: isFileDeleting,
      title: isDeleteFileFromFormType
        ? t('editSendInvite.file.delete.title')
        : t('file.delete.title'),
      submitLabel: t('file.delete.buttonLabel'),
      submitColor: 'danger',
      message: t('file.delete.message', {
        fileName: selectedFile.original_name,
      }),
    });
  }, [
    isFileDeleting,
    selectedFile,
    setConfirmationModalProps,
    setIsConfirmationModalOpen,
    handleFileDelete,
    t,
  ]);

  if (!requests.length && !isGroup) return null;

  return (
    <StyledRequests $isGroup={isGroup}>
      <StyledRequestsHead $isGroup={isGroup}>
        <EditableTitle
          title={editableTitle.editTitle}
          isEditing={editableTitle.isEditingTitle}
          onEdit={editableTitle.handleSwitchToEditMode}
          onDuplicateGroup={() => onDuplicateGroup?.(groupId || '')}
          onExpandCollapseAll={handleExpandCollapseAll}
          onChange={editableTitle.handleTitleChange}
          onSave={editableTitle.handleTitleSave}
          error={editableTitle.editTitleError}
          helperText={editableTitle.editTitleError}
          isGroup={isGroup}
          expandAllButtonState={expandAllButtonState}
          inputProps={{ 'data-testid': 'edit-group-title-input' }}
        />
      </StyledRequestsHead>
      <div>{memoizedRequests}</div>
    </StyledRequests>
  );
};

interface FollowUpRequest {
  requests: TInviteRequest[];
  disabled?: boolean;
  requestGroups: TInviteRequestGroup[];
}

export const FollowUpRequests = React.memo(
  ({ requests, disabled = false, requestGroups }: FollowUpRequest) => {
    const initialRequests = useMemo(
      () => requests?.filter((request) => !request.request_group_id) || [],
      [requests],
    );

    return (
      <FollowUpRequestsContent
        disabled={disabled}
        initialRequests={initialRequests}
        requestGroups={requestGroups}
      />
    );
  },
);
const InviteRequestsListWithForm = React.memo(
  ({
    data,
    title,
    hasDelete,
    isGroup,
    groupId,
    onUpdateGroupTitle,
    onDuplicateGroup,
  }: {
    data: TInviteRequest[];
    title?: string;
    hasDelete?: boolean;
    isGroup?: boolean;
    groupId?: string;
    onUpdateGroupTitle?: (groupId: string, newTitle: string) => void;
    onDuplicateGroup?: (groupId: string) => void;
  }) => {
    const methods = useForm<TInviteRequestsForm>({
      defaultValues: { requests: data },
      resolver: yupResolver(inviteRequestsSchema),
    });

    return (
      <FormProvider {...methods}>
        <FollowUpRequestsList
          data={data}
          title={title}
          hasDelete={hasDelete}
          isGroup={isGroup}
          groupId={groupId}
          onUpdateGroupTitle={onUpdateGroupTitle}
          onDuplicateGroup={onDuplicateGroup}
        />
      </FormProvider>
    );
  },
);

InviteRequestsListWithForm.displayName = 'InviteRequestsListWithForm';
